import { Filter } from "lucide-react";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ASPECT_OPTIONS, PLATFORM_OPTIONS, DATE_PRESETS, STATUS_FILTER_OPTIONS } from "./topicCollection";

const ANY = "__any__";
const parse = (s) => { try { return s ? new Date(s + "T00:00:00") : undefined; } catch { return undefined; } };
const fmt = (d) => { const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return local.toISOString().slice(0, 10); };

function DateFilter({ label, preset, from, to, onPreset, onRange }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={preset} onValueChange={onPreset}>
        <SelectTrigger className="mt-1 h-9" data-testid={`topic-filter-${label.toLowerCase()}-preset`}><SelectValue /></SelectTrigger>
        <SelectContent>{DATE_PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {preset === "custom" && (
        <div className="mt-2 rounded-lg border border-border/60 p-1">
          <Calendar mode="range" selected={{ from: parse(from), to: parse(to) }}
            onSelect={(r) => onRange(r?.from ? fmt(r.from) : "", r?.to ? fmt(r.to) : "")} />
          {(from || to) && <div className="px-2 pb-1 text-[11px] text-muted-foreground">{from ? format(parse(from), "d MMM") : "…"} → {to ? format(parse(to), "d MMM") : "…"}</div>}
        </div>
      )}
    </div>
  );
}

// Topics advanced filters (module-specific) rendered in the shared Collection
// toolbar. Reuses the canonical Calendar for custom ranges.
export function TopicFilters({ filters, patch, members, activeCount }) {
  const sel = (key) => (v) => patch({ [key]: v === ANY ? "" : v });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="topic-filters-trigger">
          <Filter className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Filters</span>
          {activeCount > 0 && <span className="ml-0.5 rounded-full bg-accent px-1.5 text-[10px] font-semibold text-accent-foreground" data-testid="topic-filters-count">{activeCount}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[70vh] w-80 overflow-y-auto p-4" data-testid="topic-filters-panel">
        <div className="space-y-3.5">
          <button type="button" onClick={() => patch({ needs_my_review: !filters.needs_my_review })} data-testid="topic-filter-needs-review"
            className="flex w-full items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-left text-sm hover:bg-muted/40">
            <span className={cn("flex h-4 w-4 items-center justify-center rounded border", filters.needs_my_review ? "border-accent bg-accent text-accent-foreground" : "border-border")}>{filters.needs_my_review && <span className="h-1.5 w-1.5 rounded-full bg-current" />}</span>
            Needs my review
          </button>
          <div>
            <Label className="text-xs text-muted-foreground">Topic status</Label>
            <Select value={filters.topic_status || ANY} onValueChange={sel("topic_status")}>
              <SelectTrigger className="mt-1 h-9" data-testid="topic-filter-topic-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any</SelectItem>{STATUS_FILTER_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Priority</Label>
              <Select value={filters.priority || ANY} onValueChange={sel("priority")}>
                <SelectTrigger className="mt-1 h-9" data-testid="topic-filter-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Owner</Label>
              <Select value={filters.owner_id || ANY} onValueChange={sel("owner_id")}>
                <SelectTrigger className="mt-1 h-9" data-testid="topic-filter-owner"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Anyone</SelectItem>{(members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.login_email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Has inspiration</Label>
            <Select value={filters.has_inspiration || ANY} onValueChange={sel("has_inspiration")}>
              <SelectTrigger className="mt-1 h-9" data-testid="topic-filter-has-insp"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any</SelectItem><SelectItem value="yes">Yes — has inspiration</SelectItem><SelectItem value="no">No — none yet</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Platform</Label>
              <Select value={filters.platform || ANY} onValueChange={sel("platform")}>
                <SelectTrigger className="mt-1 h-9" data-testid="topic-filter-platform"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any</SelectItem>{PLATFORM_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Aspect</Label>
              <Select value={filters.aspect || ANY} onValueChange={sel("aspect")}>
                <SelectTrigger className="mt-1 h-9" data-testid="topic-filter-aspect"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any</SelectItem>{ASPECT_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DateFilter label="Updated" preset={filters.updatedPreset} from={filters.updatedFrom} to={filters.updatedTo}
            onPreset={(v) => patch({ updatedPreset: v })} onRange={(f, t) => patch({ updatedFrom: f, updatedTo: t })} />
          <DateFilter label="Created" preset={filters.createdPreset} from={filters.createdFrom} to={filters.createdTo}
            onPreset={(v) => patch({ createdPreset: v })} onRange={(f, t) => patch({ createdFrom: f, createdTo: t })} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
